import { useEffect, useRef } from 'react';
import useSessionState from './useSessionState';
import {
  EMPTY_COMPARE_CACHE,
  compareOutputsKey,
} from '../utils/compareOutputsCache';

/**
 * Tab-scoped cache for dual-model comparison drafts.
 *
 * useSessionState only hydrates on first mount. Review pages stay mounted
 * when the clinician opens a different patient on the same route, so this
 * wrapper re-reads sessionStorage whenever the patient or stage changes.
 * Without that, patient B would briefly show patient A's fallback drafts.
 *
 * Compare mode never writes these drafts to Firestore. They live in this
 * tab only and are wiped on sign-out via clearCompareOutputsCache().
 *
 * @param {string} [patientId]
 * @param {string} [reportStage]
 * @returns {[{outputs: Object|null, showClaude: boolean, sideBySide: boolean}, Function]}
 */
export function useCompareOutputsCache(patientId, reportStage) {
  const cacheKey = patientId && reportStage
    ? compareOutputsKey(patientId, reportStage)
    : compareOutputsKey('_idle', '_idle');

  const [cache, setCache] = useSessionState(cacheKey, EMPTY_COMPARE_CACHE);

  // useSessionState already hydrates on first mount. Re-read only when the
  // namespaced key changes (patient or stage switch) so we do not fire a
  // redundant setState that trips act() warnings in tests.
  const previousKeyRef = useRef(cacheKey);
  useEffect(() => {
    if (previousKeyRef.current === cacheKey) {
      return;
    }
    previousKeyRef.current = cacheKey;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      setCache(raw != null ? JSON.parse(raw) : EMPTY_COMPARE_CACHE);
    } catch (_) {
      setCache(EMPTY_COMPARE_CACHE);
    }
  }, [cacheKey, setCache]);

  return [cache, setCache];
}
